/**
 * MobileFriendsView.js - 친구 관리 페이지
 *
 * 기능:
 * - 친구 목록 표시
 * - 친구 요청 수신/발신 관리
 * - 이름/이메일로 유저 검색 → 친구 요청
 * - 친구 삭제
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { auth } from '../../config/firebaseConfig';
import BottomNavigation from './BottomNavigation';
import { ChevronLeft, Search, UserPlus, UserCheck, UserX, X, Check, Mail, Clock } from 'lucide-react';
import { API_BASE_URL } from '../../config/apiConfig';

const API_BASE = API_BASE_URL;

async function apiCall(path, method = 'GET', body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

const Avatar = ({ user, size = 40 }) => {
  const initial = (user.firstName?.[0] || user.email?.[0] || '?').toUpperCase();
  if (user.profileImage) {
    return (
      <img
        src={user.profileImage}
        alt={initial}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: '#dbeafe',
      color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.4, flexShrink: 0,
    }}>
      {initial}
    </div>
  );
};

const UserName = ({ user }) => (
  <span>{user.firstName} {user.lastName}</span>
);

const MobileFriendsView = ({ user }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('friends'); // friends | received | sent | search
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [roomInvites, setRoomInvites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [toast, setToast] = useState('');
  const [pendingActions, setPendingActions] = useState({}); // userId → true (요청 보낸 상태)

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const loadFriends = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/api/friends');
      setFriends(data.friends || []);
      setRequests(data.requests || []);
      setSentRequests(data.sentRequests || []);
      setRoomInvites(data.roomInvites || []);
      // 부모(MobileGroupsView) 뱃지 즉시 갱신
      window.dispatchEvent(new CustomEvent('friendNotifUpdate'));
    } catch (e) {
      console.error('[MobileFriendsView] loadFriends 오류:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFriends(); }, [loadFriends]);

  // 실시간 친구 요청 수신: friend-notif 소켓 이벤트 → loadFriends
  useEffect(() => {
    const userId = user?._id || user?.id;
    if (!userId) return;
    const socket = io(API_BASE, { transports: ['websocket'], reconnectionAttempts: 3 });
    socket.on('connect', () => socket.emit('join-user', userId));
    socket.on('friend-notif', () => loadFriends());
    return () => socket.disconnect();
  }, [user, loadFriends]);

  // 검색
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await apiCall(`/api/friends/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const sendRequest = async (targetId) => {
    setPendingActions(p => ({ ...p, [targetId]: 'sending' }));
    try {
      const data = await apiCall(`/api/friends/request/${targetId}`, 'POST');
      showToast(data.msg || '친구 요청을 보냈습니다.');
      setPendingActions(p => ({ ...p, [targetId]: 'sent' }));
      loadFriends(); // 보낸 요청 목록 즉시 갱신
    } catch (e) {
      setPendingActions(p => { const n = { ...p }; delete n[targetId]; return n; });
      showToast('오류가 발생했습니다.');
    }
  };

  const acceptRequest = async (fromId) => {
    try {
      const data = await apiCall(`/api/friends/accept/${fromId}`, 'PUT');
      showToast(data.msg || '친구 요청을 수락했습니다.');
      loadFriends();
    } catch (e) { showToast('오류가 발생했습니다.'); }
  };

  const rejectRequest = async (fromId) => {
    try {
      await apiCall(`/api/friends/reject/${fromId}`, 'PUT');
      showToast('거절했습니다.');
      loadFriends();
    } catch (e) { showToast('오류가 발생했습니다.'); }
  };

  const deleteFriend = async (friendId) => {
    try {
      await apiCall(`/api/friends/${friendId}`, 'DELETE');
      showToast('친구를 삭제했습니다.');
      loadFriends();
    } catch (e) { showToast('오류가 발생했습니다.'); }
  };

  const cancelSentRequest = async (targetId) => {
    try {
      await apiCall(`/api/friends/request/${targetId}`, 'DELETE');
      loadFriends();
    } catch (e) { showToast('오류가 발생했습니다.'); }
  };

  const dismissRoomInvite = async (inviteCode) => {
    try {
      await apiCall(`/api/friends/room-invites/${inviteCode}`, 'DELETE');
      setRoomInvites(prev => prev.filter(i => i.inviteCode !== inviteCode));
    } catch (e) { showToast('오류가 발생했습니다.'); }
  };

  const joinFromInvite = async (inviteCode) => {
    await dismissRoomInvite(inviteCode);
    navigate(`/invite/${inviteCode}`);
  };

  // 이미 친구인지 / 요청 보낸 유저인지 확인
  const isFriend = (id) => friends.some(f => (f._id || f.id) === id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f8fafc' }}>
      {/* 헤더 */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
          <ChevronLeft size={24} color="#374151" />
        </button>
        <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827', flex: 1 }}>친구 등록</h1>
      </div>

      {/* 탭 */}
      <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid #e2e8f0', overflowX: 'auto' }}>
        {[
          { key: 'friends', label: '친구', count: 0 },
          { key: 'received', label: '받은 요청', count: requests.length },
          { key: 'sent', label: '보낸 요청', count: 0 },
          { key: 'search', label: '검색', count: 0 },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flexShrink: 0, padding: '12px 10px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#2563eb' : '#6b7280',
              borderBottom: tab === t.key ? '2px solid #2563eb' : '2px solid transparent',
              transition: 'all 0.15s', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
            }}
          >
            {t.label}
            {t.count > 0 && (
              <span style={{
                background: '#ef4444', color: '#fff', borderRadius: '10px',
                padding: '1px 6px', fontSize: '11px', fontWeight: 700, lineHeight: '16px',
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* 콘텐츠 */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '80px' }}>

        {/* 친구 등록 (친구 목록) */}
        {tab === 'friends' && (
          loading ? (
            <div style={centerStyle}><LoadingSpinner /></div>
          ) : friends.length === 0 ? (
            <Empty icon={<UserCheck size={40} color="#94a3b8" />} text="아직 친구가 없습니다." sub="검색 탭에서 친구를 찾아보세요." />
          ) : (
            <ul style={{ margin: 0, padding: '8px 0', listStyle: 'none' }}>
              {friends.map(f => (
                <li key={f._id || f.id} style={listItem}>
                  <Avatar user={f} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}><UserName user={f} /></div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{f.email}</div>
                  </div>
                  <button onClick={() => deleteFriend(f._id || f.id)} style={iconBtn} title="친구 삭제">
                    <UserX size={18} color="#ef4444" />
                  </button>
                </li>
              ))}
            </ul>
          )
        )}

        {/* 받은 요청 */}
        {tab === 'received' && (
          loading ? (
            <div style={centerStyle}><LoadingSpinner /></div>
          ) : requests.length === 0 ? (
            <Empty icon={<UserPlus size={40} color="#94a3b8" />} text="받은 친구 요청이 없습니다." />
          ) : (
            <ul style={{ margin: 0, padding: '8px 0', listStyle: 'none' }}>
              {requests.map(r => {
                const from = r.from || {};
                const fromId = from._id || from.id;
                return (
                  <li key={fromId} style={listItem}>
                    <Avatar user={from} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}><UserName user={from} /></div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{from.email}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => acceptRequest(fromId)} style={{ ...iconBtn, background: '#dcfce7' }} title="수락">
                        <Check size={18} color="#16a34a" />
                      </button>
                      <button onClick={() => rejectRequest(fromId)} style={{ ...iconBtn, background: '#fee2e2' }} title="거절">
                        <X size={18} color="#dc2626" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )
        )}

        {/* 보낸 요청 */}
        {tab === 'sent' && (
          loading ? (
            <div style={centerStyle}><LoadingSpinner /></div>
          ) : sentRequests.length === 0 ? (
            <Empty icon={<Clock size={40} color="#94a3b8" />} text="보낸 친구 요청이 없습니다." sub="친구 등록 탭에서 요청을 보내보세요." />
          ) : (
            <ul style={{ margin: 0, padding: '8px 0', listStyle: 'none' }}>
              {sentRequests.map(u => {
                const uid = u._id || u.id;
                const isRejected = u.requestStatus === 'rejected';
                return (
                  <li key={uid} style={listItem}>
                    <Avatar user={u} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}><UserName user={u} /></div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{u.email}</div>
                      {u.sentAt && (
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{formatSentTime(u.sentAt)}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      {isRejected ? (
                        <>
                          <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 600, padding: '4px 10px', background: '#fee2e2', borderRadius: '20px' }}>거절됨</span>
                          <button onClick={() => cancelSentRequest(uid)} style={{ ...iconBtn, background: '#fff1f2', border: '1px solid #fecaca' }} title="삭제">
                            <X size={14} color="#dc2626" />
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 600, padding: '4px 10px', background: '#fef3c7', borderRadius: '20px' }}>대기 중</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )
        )}

        {/* 검색 (닉네임/이메일로 일반 검색) */}
        {tab === 'search' && (
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '10px 14px', marginBottom: '16px' }}>
              <Search size={18} color="#94a3b8" />
              <input
                type="text"
                placeholder="닉네임 또는 이메일로 검색"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ border: 'none', outline: 'none', flex: 1, fontSize: '15px', background: 'transparent', color: '#111827' }}
                autoFocus
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex' }}>
                  <X size={16} color="#94a3b8" />
                </button>
              )}
            </div>

            {searching && <div style={centerStyle}><LoadingSpinner /></div>}

            {!searching && searchQuery && searchResults.length === 0 && (
              <Empty icon={<Search size={36} color="#94a3b8" />} text="검색 결과가 없습니다." />
            )}

            {!searching && searchResults.length > 0 && (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {searchResults.map(u => {
                  const uid = u._id || u.id;
                  const alreadyFriend = isFriend(uid);
                  const actionState = pendingActions[uid];
                  return (
                    <li key={uid} style={{ ...listItem, background: '#fff', borderRadius: '12px', marginBottom: '8px', border: '1px solid #f1f5f9' }}>
                      <Avatar user={u} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}><UserName user={u} /></div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{u.email}</div>
                      </div>
                      {alreadyFriend ? (
                        <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 600, padding: '4px 10px', background: '#dcfce7', borderRadius: '20px' }}>친구</span>
                      ) : actionState === 'sent' ? (
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, padding: '4px 10px', background: '#f1f5f9', borderRadius: '20px' }}>요청됨</span>
                      ) : (
                        <button
                          onClick={() => sendRequest(uid)}
                          disabled={actionState === 'sending'}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: actionState === 'sending' ? 'not-allowed' : 'pointer', opacity: actionState === 'sending' ? 0.6 : 1 }}
                        >
                          <UserPlus size={14} />
                          {actionState === 'sending' ? '...' : '추가'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* 토스트 */}
      {toast && (
        <div style={{ position: 'fixed', bottom: '90px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(17,24,39,0.85)', color: '#fff', padding: '10px 20px', borderRadius: '24px', fontSize: '14px', fontWeight: 500, zIndex: 9999, whiteSpace: 'nowrap', backdropFilter: 'blur(4px)' }}>
          {toast}
        </div>
      )}

      <BottomNavigation user={user} />
    </div>
  );
};

const formatSentTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return '방금 전';
  if (diffMins < 60) return `${diffMins}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  if (diffDays < 7) return `${diffDays}일 전`;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${d} ${hh}:${mm}`;
};

const LoadingSpinner = () => (
  <div style={{ width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
);

const Empty = ({ icon, text, sub }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: '12px' }}>
    {icon}
    <p style={{ margin: 0, color: '#64748b', fontSize: '15px', fontWeight: 500 }}>{text}</p>
    {sub && <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px' }}>{sub}</p>}
  </div>
);

const centerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px',
};

const listItem = {
  display: 'flex', alignItems: 'center', gap: '12px',
  padding: '12px 16px',
};

const iconBtn = {
  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px',
  padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default MobileFriendsView;
